import { EightMB, IUploadState, QueryKeys } from "@Constants";
import Result from "@Core/Result";
import SessionManager from "@Core/session";
import {
    IActivityCreationDetails,
    IPendingUpload,
    IUploadDetails,
    IUploadResultDetails,
    IUploadedDetails,
} from "@Models/IActivityDetails";
import ActivityService from "@Services/ActivityService";
import { extractDurationFromFile, generateGuid, isBlank, isNil } from "@Utils";
import { useQueryClient } from "@tanstack/react-query";
import { notification } from "antd";
import { RcFile } from "antd/lib/upload";
import moment from "moment";
import useCurrentActivityUpload from "./useCurrentActivityUpload";
import { useState } from "react";

export interface IStartUpload {
    file: RcFile;
}

interface IResumeUpload extends IStartUpload {
    uploadId: string;
}

export interface IDownloadProgressData {
    loaded: number;
    total: number;
    type: string;
}

export type IOnUploadProgress = ({
    partNumber,
    progress,
    totalParts,
}: {
    partNumber: number;
    progress: IDownloadProgressData;
    totalParts: number;
}) => void;

/**
 * Sends an error message notification.
 *
 * This message is `Unable to upload your video.`
 */
const sendUploadErrorMessage = () => {
    notification.error({ message: "Unable to upload your video." });
};

/**
 * Custom hook for handling activity uploads.
 * @returns An object containing various upload-related functions and properties.
 */
const useActivityUpload = () => {
    // get the user id from the session
    const { userId } = SessionManager.getServiceUser();

    // This hooks uploads a new `custom` activity instance.
    const queryClient = useQueryClient();

    // ** Variables ** //
    const [currentUpload, setCurrentUpload] = useState<IPendingUpload>();
    const activityService = new ActivityService();

    // ** State Hooks ** //
    const { percentage, setPercentage, uploadStatus, setUploadStatus } = useCurrentActivityUpload();

    /**
     * Reset states of an upload
     */
    const resetUpload = () => {
        setPercentage(0);
        setUploadStatus(IUploadState.Idle);
        setCurrentUpload(undefined);
    };

    /**
     * This prepares the data for a new `custom` `activityInstance`.
     * It extracts information from the file to assist a subsequent request to
     * create the activity instance.
     * @param file - The file to create the instance for.
     * @returns A promise that resolves to the created IActivityCreationDetails instance.
     */
    const createInstance = async (file: RcFile): Promise<IActivityCreationDetails | null> => {
        const duration = await extractDurationFromFile(file).catch(() => -1);

        if (duration < 0) {
            return null;
        }

        const mediaParts = Math.ceil(file.size / EightMB); // each part is 8MB
        const mediaFormat = file.name.split(".").pop();

        return {
            activityGuid: generateGuid(),
            activityName: "custom",
            creationDate: moment.utc(file.lastModified).format(),
            deviceId: navigator.userAgent,
            duration,
            mediaFormat,
            mediaParts,
            userId,
        };
    };

    /**
     * Starts the upload process for a given file.
     * @param file - The file to start the upload for.
     * @returns A promise that resolves to an object containing the activityInstanceId and uploadId.
     */
    const startUpload = async ({ file }: IStartUpload) => {
        setUploadStatus(IUploadState.Initiated);

        const model = await createInstance(file);

        if (!model) {
            return sendUploadErrorMessage();
        }

        const { lastModified, name, size, type } = file;

        const creationResult = await activityService
            .create(userId, model)
            .then((res) => {
                if (res.hasErrors) {
                    return sendUploadErrorMessage();
                }
                return res.value;
            })
            .catch(() => {
                sendUploadErrorMessage();
            });

        if (!creationResult) return {};

        setUploadStatus(IUploadState.Created);

        const { media, id: activityInstanceId } = creationResult;

        /**
         * Media is an array of IUploadDetails objects
         * Each object has the same uploadId
         */
        const uploadId = media?.[0]?.uploadId;

        if (isBlank(uploadId) || !activityInstanceId || media.length !== model.mediaParts) return {};

        const updatedCurrentUpload = {
            activityInstanceId: activityInstanceId,
            eTags: new Map(),
            fileDetails: { lastModified, name, size, type },
            media: media,
            status: IUploadState.Pending,
            totalParts: model.mediaParts,
            uploadId: uploadId,
        };

        setCurrentUpload(updatedCurrentUpload);
        setUploadStatus(IUploadState.Pending);

        //  because we are voiding the promise we need to make sure it handles its own errors
        void uploadParts(file, media, model.mediaParts, uploadId, updatedCurrentUpload);

        // we void the above promise so we can return the to the calling component immediately
        return { activityInstanceId, uploadId };
    };

    /**
     * Resumes an upload for a given file and uploadId.
     * @param file - The file to resume the upload for.
     * @param uploadId - The uploadId of the upload to resume.
     */
    const resumeUpload = ({ file, uploadId }: IResumeUpload, currentUpload: IPendingUpload) => {
        const remainingParts = currentUpload.media.filter(
            (media) => !Array.from(currentUpload.eTags.keys()).includes(media.partNumber)
        );
        setUploadStatus(IUploadState.Pending);

        // trigger the promise but don't wait for it to resolve before continuing
        void uploadParts(file, remainingParts, currentUpload.totalParts, uploadId, currentUpload);

        return { activityInstanceId: currentUpload.activityInstanceId, uploadId };
    };
    /**
     * Uploads the parts of a file.
     * @param file - The file to upload the parts for.
     * @param media - An array of IUploadDetails objects representing the parts to upload.
     * @param totalParts - The total number of parts to upload.
     * @param uploadId - The uploadId of the upload.
     */
    const uploadParts = async (
        file: RcFile,
        media: IUploadDetails[],
        totalParts: number,
        uploadId: string,
        currentUpload: IPendingUpload
    ) => {
        // All parts besides the last part are uploaded with an 8MB chunk size
        const progressMap = new Map<number, number>();

        const handleUploadProgress: IOnUploadProgress = ({ partNumber, progress, totalParts }) => {
            const percentage = (progress.loaded / progress.total) * 100;
            if (totalParts > 1) {
                progressMap.set(partNumber, percentage);
            }
            const newPercentage = () => {
                if (totalParts === 1) {
                    return Math.round(percentage);
                }
                // if we have need to add the progress of each part together
                return Math.round(Array.from(progressMap.values()).reduce((a, b) => a + b) / totalParts);
            };

            setPercentage(newPercentage());
        };

        const promises = media.map(async ({ url, partNumber }) => {
            // we don't use onSuccess here. it only runs for the final part
            return await activityService
                .uploadS3Part(file, url, partNumber, totalParts, EightMB, handleUploadProgress)
                .then((res) => {
                    if ("abort" in res) {
                        // handle aborted uploads
                        res.abort();
                        setUploadStatus(IUploadState.Aborted);
                        return Promise.reject("Upload aborted");
                    }

                    const { eTag, partNumber: partETagNumber } = res;

                    if (eTag?.length > 0) {
                        setCurrentUpload((currentUpload) => {
                            currentUpload.eTags.set(partETagNumber, eTag);
                            return currentUpload;
                        });
                        return Promise.resolve(true);
                    }
                    return Promise.reject("Invalid upload id");
                });
        });

        await Promise.all(promises)
            .then(async (res) => {
                setUploadStatus(IUploadState.Completed);
                res.every((v) => v) && (await checkForCompleteUploads(currentUpload));
            })
            .catch((err) => {
                console.error(err);
                // handle aborted uploads
                if (err === "Upload aborted") return;
                setUploadStatus(IUploadState.Error);
            });
    };

    /**
     * Aborts an upload.
     * @param upload - The upload to abort.
     * @param callback - An optional callback function to execute with the activityInstanceId.
     * @returns A promise that resolves to the result of the abort operation.
     */
    const abortUpload = async (upload: IPendingUpload, callback?: (activityInstanceId: string) => void) => {
        const {
            activityInstanceId,
            fileDetails: { name },
            uploadId,
        } = upload;

        const model: IUploadResultDetails = {
            action: "abort",
            fileFormat: name.split(".").pop() ?? "mp4",
            parts: [],
            type: "media",
            uploadId,
        };

        const result = await activityService
            .manageUpload(activityInstanceId, model)
            .then(async (res) => await tidyUpload(res, activityInstanceId, currentUpload, "abort", uploadId));

        callback?.(activityInstanceId);
        return result;
    };

    /**
     * Tidies up an upload after completion or abortion.
     * @param res - The result of the upload operation.
     * @param activityInstanceId - The activityInstanceId of the upload.
     * @returns A boolean indicating whether the tidy up operation was successful.
     */
    const tidyUpload = async (
        res: Result<IUploadedDetails>,
        activityInstanceId: string,
        currentUpload: IPendingUpload,
        // pass in the action manually in case res has an error
        action?: IUploadedDetails["action"],
        // pass uploadId manually the action manually in case res has an error
        uploadId?: string
    ) => {
        // even if it the abort fails we can remove the upload from local storage
        if ((res.hasErrors || !res.value.id) && action !== "abort") {
            sendUploadErrorMessage();
            return false;
        }
        const status = res.value.action === "complete" ? "completed" : "aborted";

        setCurrentUpload((currentUploads) => {
            currentUploads.status = status;
            return currentUploads;
        });

        if (action === "abort") {
            notification.info({
                message: `${currentUpload.fileDetails.name} has been aborted.`,
            });
        } else {
            // invalidate the activity list queries
            await queryClient.invalidateQueries([QueryKeys.ActivityListUser]);
            await queryClient.invalidateQueries([QueryKeys.ActivityListPublic]);
            notification.success({
                message: `${currentUpload.fileDetails.name} has been uploaded successfully.`,
            });
        }

        document.dispatchEvent(
            new CustomEvent("activityUpload:statusChanged", {
                cancelable: true,
                detail: { activityInstanceId, status },
            })
        );
        return true;
    };

    /**
     * Checks for complete uploads and initiates the tidy up.
     */
    const checkForCompleteUploads = async (currentUpload: IPendingUpload) => {
        if (isNil(currentUpload)) {
            return await Promise.reject("No uploads found");
        }

        const promises = Object.values([currentUpload]).map(
            async ({ activityInstanceId, eTags, fileDetails: { name }, status, totalParts, uploadId }) => {
                if (eTags.size === totalParts && status === "pending") {
                    const parts = Array.from(eTags)
                        .sort(([a], [b]) => a - b)
                        // we need to validate what's come out of local storage
                        .filter(
                            ([partNumber, eTag]) =>
                                partNumber === Number(partNumber) &&
                                partNumber > 0 &&
                                partNumber <= totalParts &&
                                eTag === String(eTag) &&
                                eTag.length > 0
                        )
                        .map((values) => ({ eTag: values[1], partNumber: values[0] }));

                    if (parts.length !== totalParts) {
                        console.error("upload incomplete"); // eslint-disable-line no-console
                        return notification.error({ message: "Unable to complete your upload." });
                    }

                    const model: IUploadResultDetails = {
                        action: "complete",
                        fileFormat: name.split(".").pop() ?? "mp4",
                        parts,
                        type: "media",
                        uploadId,
                    };

                    return await activityService
                        .manageUpload(activityInstanceId, model)
                        .then(async (res) => await tidyUpload(res, activityInstanceId, currentUpload));
                }
            }
        );

        return await Promise.all(promises);
    };

    return {
        abortUpload,
        percentage,
        resumeUpload,
        startUpload,
        resetUpload,
        uploadStatus,
        currentUpload,
    };
};

export default useActivityUpload;
